import { useEffect, useRef, useState } from "react";
import styled from "styled-components";

import SVGIcon from "src/components/ui/SVGIcon";

import { applyTheme, persistTheme, readTheme, THEMES } from "src/utils/theme";
import { isTimeExperimentEnabled, isAssetsExperimentEnabled, isMuteExperimentEnabled, isLoaderExperimentEnabled } from "src/utils/url";


const SettingsMenuContainer = styled.div`
    position: relative;
    font-weight: bold;
    color: var(--poki-blue);

    &:hover {
        text-decoration: underline;
    }
`;

const SettingsMenuTitle = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
`;

const SettingsMenuOptionsContainer = styled.div`
    position: absolute;
    bottom: calc(100% + 8px);
    right: 0;
    padding: 8px 0;
    min-width: 320px;
    background: var(--surface);
    border: 1px solid var(--border);
    box-shadow: var(--box-shadow);
    border-radius: 4px;
    z-index: 99;

    &::before {
        content: '';
        position: absolute;
        bottom: -4px;
        right: 8px;
        width: 8px;
        height: 8px;
        background: var(--surface);
        border-left: 1px solid var(--surface);
        border-bottom: 1px solid var(--surface);
        transform: rotate(45deg);
    }
`;

const SettingsMenuOption = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 8px 16px;
    color: var(--text-primary);
    cursor: pointer;

    &:hover {
        background: var(--hover);
        color: var(--poki-blue);
    }
`;

const SettingsMenuToggle = styled.span`
    position: relative;
    flex: 0 0 auto;
    margin-left: auto;
    width: 32px;
    height: 18px;
    border-radius: 999px;
    background: ${({ $on }) => ($on ? 'var(--poki-blue)' : 'var(--text-disabled)')};
    transition: background 120ms ease;

    &::after {
        content: '';
        position: absolute;
        top: 2px;
        left: ${({ $on }) => ($on ? '16px' : '2px')};
        width: 14px;
        height: 14px;
        border-radius: 50%;
        background: var(--surface);
        transition: left 120ms ease;
    }
`;

const clearLocalStorageAndRestart = () => {
    const iframe = document.querySelector('iframe');

    if (!iframe) {
        console.error('No iframe found!');
        return;
    }

    // The reload is triggered by UtilitiesModule once the SDK reports localStorageCleared.
    iframe.contentWindow.postMessage({ type: 'pokiInspectorClearLocalStorage' }, '*');
};

const toggleSearchParamAndRestart = (param) => {
    const url = new URL(window.location.href);

    if (url.searchParams.get(param) === '1') {
        url.searchParams.delete(param);
    } else {
        url.searchParams.set(param, '1');
    }

    if (url.href === window.location.href) {
        window.location.reload();
        return;
    }

    window.location.href = url.href;
};

export default function SettingsMenu() {
    const [isOpen, setIsOpen] = useState(false);
    const [theme, setTheme] = useState(readTheme);

    const containerRef = useRef(null);

    useEffect(() => {
        applyTheme(theme);
        persistTheme(theme);
    }, [theme]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        window.addEventListener('click', handleClickOutside);

        return () => {
            window.removeEventListener('click', handleClickOutside);
        };
    }, []);

    const handleIsOpen = () => {
        setIsOpen((current) => !current)
    };

    const toggleTheme = () => {
        setTheme((current) => (current === THEMES.DARK ? THEMES.LIGHT : THEMES.DARK))
    };

    const menuOptions = [
        {
            icon: 'preview',
            label: 'Dark mode',
            toggle: true,
            active: theme === THEMES.DARK,
            action: toggleTheme,
        },
        {
            icon: 'trash',
            label: 'Clear local storage',
            action: clearLocalStorageAndRestart,
        },
        {
            icon: 'clock',
            label: 'Time experiment',
            toggle: true,
            active: isTimeExperimentEnabled(),
            action: () => toggleSearchParamAndRestart('texp'),
        },
        {
            icon: 'floppy-disk',
            label: 'Assets experiment',
            toggle: true,
            active: isAssetsExperimentEnabled(),
            action: () => toggleSearchParamAndRestart('aexp'),
        },
        {
            icon: 'play',
            label: 'Mute experiment',
            toggle: true,
            active: isMuteExperimentEnabled(),
            action: () => toggleSearchParamAndRestart('mexp'),
        },
        {
            icon: 'rocket',
            label: 'Loader experiment',
            toggle: true,
            active: isLoaderExperimentEnabled(),
            action: () => toggleSearchParamAndRestart('lexp'),
        }
    ];

    return (
        <SettingsMenuContainer ref={containerRef} onClick={handleIsOpen}>
            <SettingsMenuTitle>
                <SVGIcon icon="gear" width="16px" height="16px" />
                <span>Settings</span>
            </SettingsMenuTitle>
            {isOpen && (
                <SettingsMenuOptionsContainer>
                    {menuOptions.map((option) => (
                        <SettingsMenuOption
                            key={option.label}
                            onClick={(event) => {
                                event.stopPropagation();
                                option.action();
                            }}
                        >
                            <SVGIcon icon={option.icon} width="16px" height="16px" />
                            <span>{option.label}</span>
                            {option.toggle && <SettingsMenuToggle $on={option.active} />}
                        </SettingsMenuOption>
                    ))}
                </SettingsMenuOptionsContainer>
            )}
        </SettingsMenuContainer>
    );
}
